import type { SubscriptionChangeModalProps } from "Components/Co2Common/Modals/SubscriptionChangeModal";

export const asNumber = (value: any, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
};

export const asBoolean = (value: any, fallback = false) => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value > 0;
    const normalized = String(value).toLowerCase().trim();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

export const asBillingCycle = (value: any, numMonths?: any): 'monthly' | 'annual' => {
    const normalized = String(value || '').toLowerCase().trim();
    if (normalized === 'annual' || normalized === 'yearly' || normalized === 'annually') return 'annual';
    if (normalized === 'monthly') return 'monthly';
    return Number(numMonths) === 12 ? 'annual' : 'monthly';
};

export const getLicenseField = (license: any, ...keys: string[]) => {
    for (const key of keys) {
        const value = license?.[key];
        if (value !== null && value !== undefined && value !== '') return value;
    }
    return undefined;
};

export const getCurrentPlanCode = (license: any) => {
    return String(
        getLicenseField(
            license,
            'planCode',
            'plan_code',
            'license_plan_code',
            'license_plan_name',
            'planName',
            'license_plan'
        ) || '',
    ).toLowerCase();
};

interface BuildLicenseSubscriptionModalPropsOptions {
    voucherCodeKeys?: string[];
}

type BaseSubscriptionChangeModalProps = Omit<
    SubscriptionChangeModalProps,
    'open' | 'onClose' | 'onSuccess'
>;

export const buildLicenseSubscriptionModalProps = (
    license: any,
    {
        voucherCodeKeys = ['voucher_code', 'voucherCode'],
    }: BuildLicenseSubscriptionModalPropsOptions = {},
): BaseSubscriptionChangeModalProps => ({
    editScope: 'active',
    canChangeBillingCycle: false,
    businessId: String(getLicenseField(license, 'business_id', 'businessId') || ''),
    licenseId: asNumber(getLicenseField(license, 'id'), 0),
    currentPlanCode: getCurrentPlanCode(license),
    currentUsers: asNumber(getLicenseField(license, 'allowed_users', 'allowedUsers'), 1),
    currentSites: asNumber(getLicenseField(license, 'allowed_sites', 'allowedSites'), 1),
    includedUsers: asNumber(getLicenseField(license, 'included_users', 'includedUsers', 'num_users'), 1),
    includedSites: asNumber(getLicenseField(license, 'included_sites', 'includedSites', 'num_sites'), 1),
    userPrice: Math.round(asNumber(
        getLicenseField(
            license,
            'additional_user_price_per_month',
            'additional_user_cost_per_month',
            'additionalUserCostPerMonth',
            'additional_user_cost',
            'additionalUserCost',
        ),
        0,
    ) * 100),
    sitePrice: Math.round(asNumber(
        getLicenseField(
            license,
            'additional_site_price_per_month',
            'additional_site_cost_per_month',
            'additionalSiteCostPerMonth',
            'additional_site_cost',
            'additionalSiteCost',
        ),
        0,
    ) * 100),
    currency: String(getLicenseField(license, 'currency') || 'gbp'),
    billingCycle: asBillingCycle(
        getLicenseField(license, 'billing_cycle', 'billingCycle'),
        getLicenseField(license, 'num_months', 'numMonths')
    ),
    currentContractMonths: asNumber(
        getLicenseField(license, 'plan_months', 'planMonths', 'num_months', 'numMonths'),
        getLicenseField(license, 'num_months', 'numMonths', 'billing_cycle') === 'annual' ? 12 : 1,
    ),
    currentPaymentCycleMonths: asNumber(
        getLicenseField(license, 'billing_cycle_months', 'billingCycleMonths'),
        asNumber(getLicenseField(license, 'plan_months', 'planMonths', 'num_months', 'numMonths'), 1),
    ),
    autoRenew: asBoolean(
        getLicenseField(license, 'auto_renew', 'auto_renewed', 'is_auto_renew'),
        false
    ),
    currentPeriodEnd: String(getLicenseField(license, 'end_date', 'endDate') || ''),
    currentPeriodStart: String(getLicenseField(license, 'start_date', 'startDate') || ''),
    actualUsers: asNumber(getLicenseField(license, 'actual_users', 'actualUsers'), 0),
    actualSites: asNumber(getLicenseField(license, 'actual_sites', 'actualSites'), 0),
    currentVoucherCode: String(
        getLicenseField(license, ...voucherCodeKeys) ||
        license?.voucher?.code ||
        ''
    ),
    currentCostSummary: getLicenseField(license, 'costSummary', 'cost_summary'),
});

const parseTimestamp = (value: any) => {
    if (!value) return Number.NaN;
    const time = new Date(String(value)).getTime();
    return Number.isFinite(time) ? time : Number.NaN;
};

export const getPreferredActiveLicense = (licenses: any[]) => {
    const activeLicenses = (Array.isArray(licenses) ? licenses : []).filter((item) => {
        const status = String(getLicenseField(item, 'status', 'license_status_slug') || '').toLowerCase();
        return status === 'active';
    });

    if (activeLicenses.length === 0) return null;

    const now = Date.now();
    const currentActive = activeLicenses
        .filter((item) => {
            const start = parseTimestamp(getLicenseField(item, 'start_date', 'startDate'));
            const end = parseTimestamp(getLicenseField(item, 'end_date', 'endDate'));
            return !Number.isNaN(start) && !Number.isNaN(end) && start <= now && now <= end;
        })
        .sort((a, b) => {
            const aStart = parseTimestamp(getLicenseField(a, 'start_date', 'startDate'));
            const bStart = parseTimestamp(getLicenseField(b, 'start_date', 'startDate'));
            return bStart - aStart;
        })[0];

    if (currentActive) return currentActive;

    return [...activeLicenses].sort((a, b) => {
        const aStart = parseTimestamp(getLicenseField(a, 'start_date', 'startDate'));
        const bStart = parseTimestamp(getLicenseField(b, 'start_date', 'startDate'));
        return aStart - bStart;
    })[0];
};
